/**
 * superpatronen-search.js
 *
 * Normalisiert Such-Queries für SUPERPATRONEN — zwei getrennte Pfade.
 *
 * Architektur:
 *   1. Predictive Search (Dropdown beim Tippen):
 *      Fängt /search/suggest komplett ab, feuert ZWEI parallele Calls
 *      mit jeweils eigener Normalisierung und eigener Liquid-Section,
 *      baut eine synthetische Response zusammen, die wie das Original
 *      predictive-search.liquid-Markup aussieht.
 *
 *      - Linke Spalte: 'predictive-search-printers' Section
 *        Resources: queries, collections, pages, articles
 *        Normalisierung: 'printer' (alles splitten, kein Suffix-Schutz)
 *
 *      - Rechte Spalte: 'predictive-search-products' Section
 *        Resources: products (sucht via title + tag, Drucker-Modelle
 *        stehen in den Tags der Patronen)
 *        Normalisierung: 'cartridge' (Zahl-Buchstabe-Split mit
 *        Cartridge-Suffix-Schutz, damit '207X' nicht zerstört wird)
 *
 *   2. Form-Submit (Enter im Suchfeld) → Result-Seite /search?q=…
 *      Eine Normalisierung, default 'cartridge'.
 *
 *   3. Direct-Link auf /search?q=… → einmaliger Redirect, default 'cartridge'.
 *
 * Die Result-Seite (/search?q=…) ist nicht angefasst.
 *
 * Exportiert wird ausserdem positionScore() — die Sortierlogik der
 * Drucker-Finder (Hero-Section und print-selector) — sowie
 * renderHighlighted() fuer die Trefferhervorhebung in deren Dropdowns.
 * Beide liegen hier, damit es EINE Quelle gibt statt einer Kopie pro
 * Baustein.
 *
 * ──────────────────────────────────────────────────────────────────────
 * MODI in normalize(query, mode):
 *
 *   'cartridge' (default, für Patronen-Suche & Form-Submit):
 *     Zahl-Buchstabe-Split mit Cartridge-Suffix-Schutz.
 *     'HP 207X' bleibt 'HP 207X', '305A' bleibt '305A', '924E' bleibt.
 *     'm479dw' wird zu 'm 479 dw' (dw kein Suffix).
 *
 *   'printer' (für Drucker-Suche):
 *     Zahl-Buchstabe-Split immer aktiv.
 *     'M404x' wird zu 'M 404 x', '207X' wird zu '207 X'.
 *
 * Beide Modi machen identisch: Buchstabe-Zahl-Split, Serien-Alias-Auflösung
 * (Regel A3), Marken-Split (aggressive), Serien-Split (boundary).
 *
 * MATCH-MODI in insertSpacesAround:
 *
 *   'aggressive' (für Marken):
 *     Splittet vor und nach dem Token bei jedem Nicht-Whitespace.
 *
 *   'boundary' (für Serien):
 *     Splittet nur nach dem Token, und nur wenn vor dem Token bereits
 *     eine Wortgrenze steht. Verhindert, dass 'xpress' in 'express'
 *     fälschlich erkannt wird.
 * ──────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════
  // DATEN
  // ═══════════════════════════════════════════════════════════════════

  const BRANDS = [
    'Brother', 'Canon', 'Dell', 'Develop', 'Epson', 'G&G', 'HP', 'IBM',
    'Kodak', 'Konica', 'Kyocera', 'Lexmark', 'OKI', 'Olivetti', 'Panasonic',
    'Philips', 'Ricoh', 'Samsung', 'Sharp', 'Tally', 'Toshiba', 'Utax', 'Xerox'
  ];

  const SERIES = [
    'LaserJet', 'DeskJet', 'Pixma', 'i-SENSYS', 'OfficeJet', 'PhotoSmart',
    'imageRUNNER', 'WorkForce', 'EcoTank', 'Aficio', 'P-Touch', 'ECOSYS',
    'Stylus', 'PSC', 'DesignJet', 'WorkCentre', 'Phaser', 'imagePROGRAF',
    'Minolta', 'MFC-J', 'Xpress', 'Microline', 'SureColor', 'HL-L',
    'Optra', 'Ineo', 'VersaLink', 'TASKalfa', 'MFC-L', 'Aculaser',
    'iR-C', 'Infoprint', 'ProXpress', 'PageWide', 'Maxify', 'MX-M',
    'DCP-L', 'KX-MB', 'MultiXpress', 'iR-ADV', 'DCP-J', 'BJC',
    'TM-U', 'Selphy', 'Intellifax', 'SL-M', 'Multipass', 'KX-FLB',
    'Genicom', 'FS-C', 'MX-B', 'Lasershot', 'D-Color', 'ECR',
    'KX-FL', 'PLQ', 'P-C', 'AltaLink'
  ];

  // Patronen-Suffixe — nur relevant in mode='cartridge'.
  const CARTRIDGE_SUFFIXES = [
    'XL', 'XXL', 'XXXL', 'HC',
    'A',  // HP Standard (122A, 305A, 410A, 504A, 826A, …)
    'X',  // HP High-Yield (201X, 207X, 410X, 508X, 656X, 973X, …)
    'H',  // Canon / Lexmark (045H, 067H, 702H, 802H)
    'L',  // Epson / Canon (408L, T10L)
    'S',  // Lexmark (802S)
    'Z',  // HP (217Z)
    'E'   // HP (924E)
  ];

  // Geklebte Schreibweisen von Serien, die eigentlich einen Bindestrich
  // tragen. Regel A trennt ausschliesslich Buchstabe<->Ziffer. Bei
  // 'mfcj5330dw' bleibt der Buchstabenblock 'mfcj' deshalb zusammen,
  // waehrend der Shopify-Index die Terme 'mfc' und 'j' kennt — kein
  // Treffer. Es gibt keine generische Regel fuer Buchstabe<->Buchstabe
  // (woher soll der Code wissen, dass bei 'mfc|j' getrennt wird und nicht
  // bei 'mf|cj'?), deshalb diese explizite Tabelle.
  //
  // printerOnly: Alias greift nur in mode='printer'. Ausschliesslich fuer
  // 'pc' gesetzt. In mode='cartridge' wuerde 'pc201' sonst zu 'P-C 201'
  // und die Brother PC-201 / PC-204RF Thermo-Transfer-Rollen waeren in
  // der Produktsuche nicht mehr auffindbar.
  //
  // Bewusst NICHT aufgenommen: 'MX-M' — steht in SERIES, im Sortiment
  // wurden dazu aber keine Collections gefunden.
  const SERIES_ALIASES = [
    { glued: 'isensys', canonical: 'i-SENSYS' },
    { glued: 'iradv',   canonical: 'iR-ADV' },
    { glued: 'kxflb',   canonical: 'KX-FLB' },
    { glued: 'ptouch',  canonical: 'P-Touch' },
    { glued: 'dcolor',  canonical: 'D-Color' },
    { glued: 'mfcj',    canonical: 'MFC-J' },
    { glued: 'mfcl',    canonical: 'MFC-L' },
    { glued: 'dcpj',    canonical: 'DCP-J' },
    { glued: 'dcpl',    canonical: 'DCP-L' },
    { glued: 'kxmb',    canonical: 'KX-MB' },
    { glued: 'kxfl',    canonical: 'KX-FL' },
    { glued: 'hll',     canonical: 'HL-L' },
    { glued: 'fsc',     canonical: 'FS-C' },
    { glued: 'mxb',     canonical: 'MX-B' },
    { glued: 'slm',     canonical: 'SL-M' },
    { glued: 'tmu',     canonical: 'TM-U' },
    { glued: 'irc',     canonical: 'iR-C' },
    { glued: 'pc',      canonical: 'P-C', printerOnly: true }
  ];

  const SORTED_BRANDS = [...BRANDS].sort((a, b) => b.length - a.length);
  const SORTED_SERIES = [...SERIES].sort((a, b) => b.length - a.length);

  // ═══════════════════════════════════════════════════════════════════
  // NORMALIZE
  // ═══════════════════════════════════════════════════════════════════

  function normalize(query, mode) {
    if (!query || typeof query !== 'string') return query;
    let q = query.trim();
    if (!q) return query;
    mode = mode || 'cartridge';

    // Regel A (Teil 1): Buchstabe→Zahl trennen.
    //   'ts8550' → 'ts 8550'
    q = q.replace(/([a-zA-Z])(\d)/g, '$1 $2');

    // Regel A (Teil 2): Zahl→Buchstabe trennen.
    //   mode='cartridge': Suffix-Schutz aktiv ('207x' bleibt)
    //   mode='printer':   immer splitten ('207x' wird zu '207 x')
    q = q.replace(/(\d+)([a-zA-Z]+)/g, (match, digits, letters) => {
      if (mode === 'cartridge' && CARTRIDGE_SUFFIXES.includes(letters.toUpperCase())) {
        return match;
      }
      return digits + ' ' + letters;
    });

    // Regel A3: geklebte Serienkuerzel in die kanonische Schreibweise
    // ueberfuehren — 'mfcj 5330 dw' → 'MFC-J 5330 dw'.
    // Laeuft nach Regel A, weil der Buchstabenblock dann bereits als
    // eigenes Token freisteht. Ersetzt wird nur ein VOLLSTAENDIGES Token
    // (Lookahead auf Whitespace oder Stringende), damit Teilstrings in
    // laengeren Woertern unberuehrt bleiben: 'mfcjx' bleibt 'mfcjx'.
    for (const alias of SERIES_ALIASES) {
      if (alias.printerOnly && mode !== 'printer') continue;
      const escapedGlued = alias.glued.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      q = q.replace(
        new RegExp(`(^|\\s)(${escapedGlued})(?=\\s|$)`, 'gi'),
        (match, prefix) => prefix + alias.canonical
      );
    }

    // Regel B: Marken aus zusammengeschriebenen Wörtern lösen.
    q = insertSpacesAround(q, SORTED_BRANDS, 'aggressive');

    // Regel C: Serien aus zusammengeschriebenen Wörtern lösen.
    q = insertSpacesAround(q, SORTED_SERIES, 'boundary');

    q = q.replace(/\s+/g, ' ').trim();
    return q;
  }

  function insertSpacesAround(query, tokens, mode) {
    let q = query;
    for (const token of tokens) {
      const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      if (mode === 'aggressive') {
        q = q.replace(new RegExp(`(\\S)(${escaped})`, 'gi'), '$1 $2');
        q = q.replace(new RegExp(`(${escaped})(\\S)`, 'gi'), '$1 $2');
      } else if (mode === 'boundary') {
        // Vor Token muss (^|\s|\d) stehen, nach Token Nicht-Whitespace.
        // Verhindert Substring-Matches wie 'xpress' in 'express'.
        q = q.replace(
          new RegExp(`(^|\\s|\\d)(${escaped})(\\S)`, 'gi'),
          '$1$2 $3'
        );
      }
    }
    return q;
  }

  // ═══════════════════════════════════════════════════════════════════
  // RANKING
  // ═══════════════════════════════════════════════════════════════════

  function tokenizeForRanking(str) {
    return String(str || '').toLowerCase().split(/[\s\-_]+/).filter(Boolean);
  }

  /**
   * Bewertet, wie gut die REIHENFOLGE und NAEHE der Query-Tokens zu einem
   * Titel passt. Hoeher ist besser. Reine Sortierhilfe — es wird nichts
   * ausgefiltert, das erledigen die Aufrufer selbst.
   *
   * Hintergrund: Im Sortiment gibt es Modellfamilien, die dieselben Terme
   * in unterschiedlicher Reihenfolge tragen, z. B.
   *   'Brother MFC-J 4550 DW'  (J als Serie)
   *   'Brother MFC-4550 J'     (J als Suffix, altes Faxgeraet)
   * Fuer den Shopify-Index sind beide gleichwertig 'mfc' + 'j' + '4550'.
   * Wer 'mfc j' tippt, meint die erste Familie; wer 'mfc 4550 j' tippt,
   * die zweite. Genau das bildet diese Funktion ab.
   *
   * Verfahren:
   *   1. Query-Tokens der Reihe nach im Titel suchen, jedes weitere erst
   *      HINTER der vorigen Fundstelle. Teilstring-Match wie in den
   *      Filtern der Aufrufer, damit die Sortierung auch waehrend des
   *      Tippens stabil bleibt ('mfc j 45' muss schon greifen).
   *   2. Alle in Reihenfolge gefunden: Abzug pro Luecke zwischen erstem
   *      und letztem Treffer, plus kleiner Abzug fuer spaeten Start.
   *   3. Reihenfolge verletzt, aber alle Tokens vorhanden: fester
   *      Mittelwert — landet hinter allen sauberen Treffern, faellt
   *      aber nicht raus.
   *   4. Token fehlt: 0.
   */
  function positionScore(title, query) {
    const titleTokens = tokenizeForRanking(title);
    const queryTokens = tokenizeForRanking(query);
    if (queryTokens.length === 0 || titleTokens.length === 0) return 0;

    const positions = [];
    let cursor = 0;
    let ordered = true;

    for (const q of queryTokens) {
      let found = -1;
      for (let i = cursor; i < titleTokens.length; i++) {
        if (titleTokens[i].indexOf(q) !== -1) { found = i; break; }
      }
      if (found === -1) { ordered = false; break; }
      positions.push(found);
      cursor = found + 1;
    }

    if (ordered) {
      const span = positions[positions.length - 1] - positions[0] + 1;
      const gap = span - queryTokens.length;
      return 1000 - (gap * 100) - positions[0];
    }

    const allPresent = queryTokens.every(q =>
      titleTokens.some(t => t.indexOf(q) !== -1)
    );
    return allPresent ? 200 : 0;
  }

  // ═══════════════════════════════════════════════════════════════════
  // HIGHLIGHTING
  // ═══════════════════════════════════════════════════════════════════

  function escapeForRegex(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Schreibt einen Titel mit hervorgehobenen Treffern in ein Zielelement.
   *
   * WARUM DOM STATT HTML-STRING:
   * Vorher hatte jeder Finder eine eigene highlightMatch(), die den Titel
   * als HTML-String zusammenbaute: pro Suchbegriff ein String-Replace mit
   * <strong>$1</strong>, nacheinander auf dasselbe Ergebnis angewendet,
   * danach per innerHTML gesetzt. Ab dem zweiten Durchlauf enthielt der
   * String bereits Markup, und die Ersetzung unterschied nicht zwischen
   * Titeltext und Tag. Ein Ein-Buchstaben-Begriff wie 'n' (entsteht aus
   * 'CP1514n', das normalize() im printer-Modus zu 'CP 1514 n' aufteilt)
   * traf damit auch das 'n' im Tag selbst. Der Tag zerbrach, der
   * HTML-Parser verwarf ihn, und im Dropdown stand sichtbar 'ng>'.
   * Betroffen war jeder Begriff aus den Buchstaben s, t, r, o, n, g —
   * unabhaengig von Marke und Modell.
   *
   * Hier wird nie ein HTML-String gebaut: der Titel wird EINMAL an allen
   * Treffern zerlegt, Textteile gehen per textContent ins DOM, Treffer als
   * <strong>-Element mit textContent. Damit existiert kein Markup, in das
   * eine Ersetzung hineinlaufen koennte, und der Collection-Titel aus
   * /search/suggest.json wird nirgends als HTML interpretiert.
   *
   * @param {Element} target  Zielelement, sein Inhalt wird ersetzt.
   * @param {string}  title   Anzuzeigender Titel (externe Daten).
   * @param {Array}   tokens  Zu markierende Begriffe. Der Aufrufer
   *                          bestimmt, was hineingeht (Marke + Query oder
   *                          nur Query), weil die Finder unterschiedlich
   *                          tokenisieren.
   *
   * Fehlerverhalten: in jedem Zweifelsfall steht am Ende der reine Titel
   * als Text im Element. Es gibt keinen Pfad, der das Element leer laesst
   * oder Markup ausgibt.
   */
  function renderHighlighted(target, title, tokens) {
    if (!target) return;

    const text = (title === null || title === undefined) ? '' : String(title);

    // Basiszustand: reiner Text. Jeder folgende Abbruch faellt hierauf
    // zurueck, deshalb steht die Zuweisung ganz oben.
    target.textContent = text;
    if (!text) return;

    const list = (tokens || [])
      .map(t => (t === null || t === undefined) ? '' : String(t).trim())
      .filter(Boolean);
    if (list.length === 0) return;

    // Laengste zuerst: bei einer Alternation greift in JavaScript die
    // erste passende Variante. Ohne Sortierung wuerde 'cp' vor 'cp1514'
    // matchen und den laengeren Treffer zerschneiden.
    const unique = Array.from(new Set(list)).sort((a, b) => b.length - a.length);

    let regex;
    try {
      regex = new RegExp('(' + unique.map(escapeForRegex).join('|') + ')', 'gi');
    } catch (e) {
      console.warn('[SUPERPATRONEN Search] highlight regex failed:', e);
      return;
    }

    // split mit GENAU EINER Capture-Group liefert abwechselnd Text und
    // Treffer: [text, treffer, text, treffer, …, text]. Ungerade Indizes
    // sind damit immer Treffer.
    const parts = text.split(regex);
    if (parts.length < 2) return;

    target.textContent = '';
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part) continue;
      if (i % 2 === 1) {
        const strong = document.createElement('strong');
        strong.textContent = part;
        target.appendChild(strong);
      } else {
        target.appendChild(document.createTextNode(part));
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // INTEGRATION 1: Fetch-Intercept mit Two-Call-Architektur
  // ═══════════════════════════════════════════════════════════════════

  // Hält den AbortController des aktuell laufenden Predictive-Search-Calls.
  // Bei einem neuen Call wird der alte abgebrochen, damit stale Antworten
  // nicht über aktuelle Ergebnisse drüberrendern.
  let activePredictiveSearchController = null;

  const originalFetch = window.fetch;

  /**
   * Entscheidet, ob ein fetch-Call abgefangen wird.
   *
   * Abgefangen wird ausschliesslich der Section-Rendering-Pfad
   * /search/suggest — auch mit Locale-Praefix, z. B. /de/search/suggest.
   * Das ist exakt der Pfad, den routes.predictive_search_url in
   * predictive-search.js liefert (Hauptsuche im Header).
   *
   * NICHT abgefangen wird /search/suggest.json. Dieser Ajax-Endpoint
   * liefert JSON und wird von anderen Bausteinen genutzt, z. B. dem
   * Druckermodell-Finder in print-selector.liquid. Die Two-Call-
   * Architektur antwortet mit HTML — ein JSON-Consumer scheitert daran
   * mit einem Parse-Fehler. Diese Bausteine rufen normalize() direkt auf.
   */
  function isPredictiveSearchSectionUrl(url) {
    if (!url || typeof url !== 'string') return false;
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      return /(^|\/)search\/suggest$/.test(pathname);
    } catch (e) {
      return false;
    }
  }

  window.fetch = function (input, options) {
    try {
      const url = (typeof input === 'string') ? input : (input && input.url);
      if (isPredictiveSearchSectionUrl(url)) {
        // Original-Call abfangen und durch Two-Call-Architektur ersetzen.
        return handlePredictiveSearch(url, input, options);
      }
    } catch (e) {
      console.warn('[SUPERPATRONEN Search] fetch-intercept failed, passing through:', e);
    }
    return originalFetch.call(this, input, options);
  };

  /**
   * Macht zwei parallele /search/suggest Calls (Drucker + Produkte) und
   * baut daraus eine synthetische Response zusammen, die wie das Original-
   * predictive-search.liquid-Markup aussieht.
   */
  function handlePredictiveSearch(originalUrl, originalInput, originalOptions) {
    const urlObj = new URL(originalUrl, window.location.origin);
    const originalQ = urlObj.searchParams.get('q') || '';
    const originalSection = urlObj.searchParams.get('section_id');

    // Wenn der Call schon auf eine unserer Sub-Sections zielt, durchreichen
    // (verhindert Endlosschleife, falls etwas die Sub-Sections direkt callt).
    if (originalSection === 'predictive-search-printers'
        || originalSection === 'predictive-search-products') {
      return originalFetch.call(window, originalInput, originalOptions);
    }

    // Vorigen Call abbrechen, falls noch in flight. Neuer Controller für
    // diesen Call. Das Signal wird beiden Sub-Calls mitgegeben.
    if (activePredictiveSearchController) {
      activePredictiveSearchController.abort();
    }
    const controller = new AbortController();
    activePredictiveSearchController = controller;

    // Basis-URL bauen: alle Original-Parameter übernehmen, q und section_id
    // werden pro Sub-Call überschrieben.
    function buildSubCallUrl(qValue, sectionId, resourceTypes) {
      const sub = new URL(urlObj.toString());
      sub.searchParams.set('q', qValue);
      sub.searchParams.set('section_id', sectionId);
      sub.searchParams.set('resources[type]', resourceTypes);
      return sub.toString();
    }

    const printerQ = normalize(originalQ, 'printer');
    const productQ = normalize(originalQ, 'cartridge');

    const printerUrl = buildSubCallUrl(
      printerQ,
      'predictive-search-printers',
      'query,collection,page,article'
    );
    const productUrl = buildSubCallUrl(
      productQ,
      'predictive-search-products',
      'product'
    );

    // Parallele Calls mit Abort-Signal. Promise.all wartet beide ab.
    const fetchOpts = { signal: controller.signal };
    const printerPromise = originalFetch.call(window, printerUrl, fetchOpts)
      .then(r => r.ok ? r.text() : '');
    const productPromise = originalFetch.call(window, productUrl, fetchOpts)
      .then(r => r.ok ? r.text() : '');

    return Promise.all([printerPromise, productPromise]).then(([printerHtml, productHtml]) => {
      // Doppelt prüfen: wurde der Call zwischenzeitlich abgebrochen?
      // Dann pending Promise zurückgeben, damit Datei 1 nicht mit stale
      // Daten rendert. Der nachfolgende Call übernimmt.
      if (controller.signal.aborted) {
        return new Promise(() => {});
      }
      const synthetic = buildSyntheticResponse(printerHtml, productHtml, originalQ);
      return new Response(synthetic, {
        status: 200,
        headers: { 'Content-Type': 'text/html; charset=utf-8' }
      });
    }).catch(err => {
      // AbortError ist erwartet, wenn ein neuer Call kommt → pending Promise,
      // damit kein .catch() in Datei 1 das Dropdown schließt.
      if (err && err.name === 'AbortError') {
        return new Promise(() => {});
      }
      console.warn('[SUPERPATRONEN Search] sub-call failed, falling back to single call:', err);
      // Fallback: Original-Call mit cartridge-Normalisierung durchführen.
      urlObj.searchParams.set('q', productQ);
      return originalFetch.call(window, urlObj.toString(), originalOptions);
    });
  }

  /**
   * Konstruiert ein synthetisches HTML-Dokument, das die gleiche Struktur
   * hat wie das Original predictive-search.liquid (damit Datei 1 ohne
   * Änderung weiterläuft).
   */
  function buildSyntheticResponse(printerHtml, productHtml, originalTerm) {
    // Sub-Section-Inhalt extrahieren und LIs zählen — in einem Parse pro Call.
    const printerData = extractSectionData(printerHtml, 'predictive-search-printers');
    const productData = extractSectionData(productHtml, 'predictive-search-products');

    const printerGroup = printerData.content;
    const productGroup = productData.content;
    const totalCount = printerData.count + productData.count;

    const hasPrinter = printerGroup.trim().length > 0;
    const hasProduct = productGroup.trim().length > 0;
    const twoColumnClass = (hasPrinter && hasProduct)
      ? 'predictive-search-results-group-wrapper-two-column'
      : '';
    const emptyClass = (totalCount === 0)
      ? 'predictive-search-results-list-empty'
      : '';

    // aria-labelledby dynamisch setzen: zeigt nur auf Headings, die
    // tatsächlich im DOM gerendert werden. Bei leerem Ergebnis ganz weglassen.
    // Vermeidet kaputte ARIA-Referenzen für Screen-Reader.
    const headingIds = [];
    if (hasPrinter) headingIds.push('predictive-search-printers-heading');
    if (hasProduct) headingIds.push('predictive-search-products-heading');
    const ariaLabelledByAttr = headingIds.length > 0
      ? `aria-labelledby="${headingIds.join(' ')}"`
      : '';

    // Term escapen für den "Suche nach"-Button.
    const safeTerm = escapeHtml(originalTerm || '');

    // Synthetisches Markup: identische Struktur zum Original-Liquid.
    return `<!DOCTYPE html><html><body><div id="shopify-section-predictive-search" class="shopify-section">
<div id="predictive-search-results">
  <div
    id="predictive-search-results-list"
    class="predictive-search-results-list ${emptyClass}"
    role="listbox"
    ${ariaLabelledByAttr}
  >
    <span class="spinner-xs"></span>
    <div class="predictive-search-results-group-wrapper ${twoColumnClass}">
      ${printerGroup}
      ${productGroup}
    </div>
  </div>
  <div
    id="predictive-search-option-search-keywords"
    class="predictive-search-list-item-term"
    role="option"
  >
    <button
      class="predictive-search-item-button predictive-search-item-term typograph-body"
      tabindex="-1"
    >
      Suche nach &quot;${safeTerm}&quot;
      <span class="angle angle-right"><span class="angle-icon"></span></span>
    </button>
  </div>
  ${totalCount === 0 ? '<div class="predictive-search-loading-state" aria-hidden="true"><span class="spinner-xs"></span></div>' : ''}
</div>
<span class="hidden" data-predictive-search-live-region-count-value>${totalCount}</span>
</div></body></html>`;
  }

  /**
   * Extrahiert in EINEM Parse-Vorgang sowohl den Section-innerHTML als auch
   * die Anzahl der <li>-Elemente. Spart einen DOMParser-Aufruf gegenüber
   * getrennter Extraction und Zählung.
   */
  function extractSectionData(html, sectionId) {
    if (!html) return { content: '', count: 0 };
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector(`#shopify-section-${sectionId}`);
      if (!section) return { content: '', count: 0 };
      return {
        content: section.innerHTML,
        count: section.querySelectorAll('li').length
      };
    } catch (e) {
      return { content: '', count: 0 };
    }
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ═══════════════════════════════════════════════════════════════════
  // INTEGRATION 2: Form-Submit (Result-Seite)
  // ═══════════════════════════════════════════════════════════════════
  // Beim Enter wird der Input-Wert kurz vor Submit auf die normalisierte
  // Variante gesetzt (mode='cartridge', weil Result-Seite Patronen-Produkte
  // zeigt).

  function attachFormHandlers(root) {
    const forms = (root || document).querySelectorAll('form[action*="/search"]');
    forms.forEach(form => {
      if (form.__superpatronenAttached) return;
      form.__superpatronenAttached = true;

      form.addEventListener('submit', function () {
        const input = form.querySelector('input[name="q"]');
        if (!input) return;
        const original = input.value.trim();
        if (!original) return;
        const normalized = normalize(original, 'cartridge');
        if (normalized !== original) {
          input.value = normalized;
        }
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // INTEGRATION 3: Direct-Link-Redirect
  // ═══════════════════════════════════════════════════════════════════

  function handleDirectLink() {
    if (!window.location.pathname.includes('/search')) return;
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (!q) return;
    if (params.get('_sp') === '1') return;
    const normalized = normalize(q, 'cartridge');
    if (normalized !== q) {
      params.set('q', normalized);
      params.set('_sp', '1');
      const newUrl = window.location.pathname + '?' + params.toString() + window.location.hash;
      window.location.replace(newUrl);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // BOOTSTRAP
  // ═══════════════════════════════════════════════════════════════════

  function init() {
    handleDirectLink();
    attachFormHandlers();
    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) attachFormHandlers(node);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Debugging in der Browser-Konsole:
  //   SuperpatronenSearch.normalize('hp207x', 'cartridge')     → 'hp 207x'
  //   SuperpatronenSearch.normalize('hp207x', 'printer')       → 'hp 207 x'
  //   SuperpatronenSearch.normalize('m404x', 'cartridge')      → 'm 404x'
  //   SuperpatronenSearch.normalize('m404x', 'printer')        → 'm 404 x'
  //   SuperpatronenSearch.normalize('mfcj5330dw', 'printer')   → 'MFC-J 5330 dw'
  //   SuperpatronenSearch.normalize('hll2350dw', 'printer')    → 'HL-L 2350 dw'
  //   SuperpatronenSearch.normalize('mfcjx100', 'printer')     → 'mfcjx 100'  (kein Teilstring-Match)
  //   SuperpatronenSearch.normalize('pc201', 'cartridge')      → 'pc 201'   (Brother-Rolle bleibt)
  //   SuperpatronenSearch.normalize('pc2655w', 'printer')      → 'P-C 2655 w'
  //
  //   SuperpatronenSearch.positionScore('Brother MFC-J 4550 DW', 'mfc j')      → 999
  //   SuperpatronenSearch.positionScore('Brother MFC-4550 J',    'mfc j')      → 899
  //   SuperpatronenSearch.positionScore('Brother MFC-4550 J',    'mfc 4550 j') → 999
  //   SuperpatronenSearch.positionScore('Brother MFC-J 4550 DW', 'mfc 4550 j') → 200
  //
  //   SuperpatronenSearch.renderHighlighted(el, 'HP Color LaserJet CP 1514 N',
  //     ['hp', 'color', 'laserjet', 'cp', '1514', 'n'])
  window.SuperpatronenSearch = { normalize, positionScore, renderHighlighted };
})();
